import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import {
	useCompanyHotelQuery,
	useCompanyHotelsLiteQuery,
	useUpdateCompanyHotelSubscriptionLastLoginDateMutation,
} from '@app/model/company_hotel/service/CompanyHotelServiceHooks';
import queryString from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSearchParam } from 'react-use';
import useSiteState from '@app/state/SiteState';
import _orderBy from 'lodash/orderBy';
import CompanyHotelLite from '@app/model/company_hotel/CompanyHotelLite';

export const CompanyHotelsContext = React.createContext<{
	companyHotels: CompanyHotelLite[],
	selectedCompanyHotel: CompanyHotel | undefined
}>({ companyHotels: [], selectedCompanyHotel: undefined });

const CompanyHotelsProvider: React.FunctionComponent<PropsWithChildren<{
	loadingPlaceholder: React.ReactNode
}>> = ({ children, loadingPlaceholder }) => {

	const { data, isLoading, isSuccess } = useCompanyHotelsLiteQuery({
		refetchOnWindowFocus: false,
	});


	if (isLoading) return loadingPlaceholder;

	if (isSuccess) {

		return <DecideSelectedCompanyHotel companyHotels={data}>
			{children}
		</DecideSelectedCompanyHotel>;
	}

	return <></>;
};

const DecideSelectedCompanyHotel: React.FunctionComponent<PropsWithChildren<{ companyHotels: CompanyHotelLite[] }>> = ({
																																																												 companyHotels,
																																																												 children,
																																																											 }) => {

	const siteState = useSiteState();
	const webhotelierId = useSearchParam('webhotelierId');
	const navigate = useNavigate();
	const location = useLocation();
	const [isWebhotelierProcessed, setIsWebhotelierProcessed] = useState(false);

	if (!!webhotelierId && !isWebhotelierProcessed) {
		const companyHotel = companyHotels?.find(ch => ch.webhotelierId == webhotelierId);
		if (!!companyHotel) {
			siteState.setSelectedCompanyHotelId(companyHotel!.id);
			navigate(location.pathname, { replace: true });
		}
		setIsWebhotelierProcessed(true);
		if (!isWebhotelierProcessed) return null;
	}

	return <Provider companyHotels={companyHotels}>
		{children}
	</Provider>;

};

const Provider: React.FunctionComponent<PropsWithChildren<{ companyHotels: CompanyHotelLite[] }>> = ({
																																																			 companyHotels,
																																																			 children,
																																																		 }) => {
	const { hash } = useLocation();

	const siteState = useSiteState();

	const selectedCompanyHotelId = useMemo(() => {
		// an EMPTY array is truthy, so `!companyHotels` never caught it and
		// `companyHotels[0].id` below threw, taking the whole SPA down through the
		// root error boundary. A profile with no readable hotel is a legitimate
		// state (see the WebHotelier SSO fix) - render nothing, do not crash.
		if (!companyHotels || companyHotels.length === 0) return undefined;

		let companyHotelId;

		if (!!hash) {
			const params = queryString.parse(hash);
			companyHotelId = params.companyHotelId;
		} else {
			companyHotelId = siteState.selectedCompanyHotelId;
		}

		return companyHotels.find(ch => ch.id === Number(companyHotelId))?.id ?? companyHotels[0].id;
	}, [companyHotels, hash, siteState.selectedCompanyHotelId]);

	// bail out BEFORE the state write: Number(undefined) is NaN, NaN is falsy and never equal
	// to itself, so storing it would re-trigger the write on every render
	if (selectedCompanyHotelId === undefined) return null;

	const numericSelectedCompanyHotelId = Number(selectedCompanyHotelId);
	if (!Number.isFinite(numericSelectedCompanyHotelId)) return null;

	if (siteState.selectedCompanyHotelId === undefined || siteState.selectedCompanyHotelId !== numericSelectedCompanyHotelId) {
		siteState.setSelectedCompanyHotelId(numericSelectedCompanyHotelId);
	}

	return <LoadCompanyHotel companyHotels={companyHotels} selectedCompanyHotelId={numericSelectedCompanyHotelId}>
		{children}
	</LoadCompanyHotel>;

};

const LoadCompanyHotel: React.FunctionComponent<PropsWithChildren<{
	companyHotels: CompanyHotelLite[],
	selectedCompanyHotelId: number
}>>
	= ({ companyHotels, selectedCompanyHotelId, children }) => {

	const { data, isLoading, isSuccess } = useCompanyHotelQuery(selectedCompanyHotelId);
	const { mutate: updateLastLoginDate } = useUpdateCompanyHotelSubscriptionLastLoginDateMutation();

	useEffect(() => {
		if (!isSuccess) return;
		updateLastLoginDate({ companyHotelId: selectedCompanyHotelId });
	}, [isSuccess, selectedCompanyHotelId, updateLastLoginDate]);

	if (isLoading) {
		return null;
	}

	if (!isSuccess) return null;

	else {

	const sort = (companyHotels: CompanyHotelLite[]) => {
		return _orderBy(companyHotels, 'mainHotelName', 'asc');
	};

	return <CompanyHotelsContext.Provider value={{
		companyHotels: sort(companyHotels),
		selectedCompanyHotel: data,
	}}>
		{children}
	</CompanyHotelsContext.Provider>;
		}
};


export default CompanyHotelsProvider;
